import { COLORTYPES_NAMES } from "../../../utils/colors";
import { FILEEDITOR, FILEEDITORTYPES, FILEEDITORTYPENAMES } from "./fileEditor";
import { REACT_APP_COP_SERVER } from "../../../utils/env";

import sha1 from "js-sha1";

const extractFormState = (formState) => {
  let fileJson = {};
  if (formState) {
    fileJson.width = parseInt(formState.width);
    fileJson.height = parseInt(formState.height);
    fileJson.underglass = formState.underglass;
    fileJson.amount = parseInt(formState.amount);
    fileJson.materialSerie = formState.materialSerie;
    fileJson.editorType = formState.editorType;
    fileJson.color = formState.color && formState.color.value;
    fileJson.laminate = formState.laminate;
    fileJson.cutting = formState.cutting;
    fileJson.border = parseInt(formState.border);
    fileJson.filecheck = formState.filecheck;
    fileJson.keepRatio = formState.keepRatio;
    fileJson.fileRatio = formState.fileRatio;
    fileJson.whiteprint_mode = formState.whiteprint_mode;
    fileJson.vectorizeFile = formState.vectorizeFile;
    fileJson.skip_file_upload = formState.skip_file_upload;
  }
  return fileJson;
};

function getFoilBasePrice(state, currentEditor) {
  const { editorType, color } = state;
  let price = 45; // fallback value
  if (editorType === FILEEDITORTYPES.CUTTINGPLOTT) {
    price = color.pricePerSqm || price;
  } else {
    price = currentEditor.pricePerSqm;
  }
  return price;
}

function getSurfaceSizeForPrice(fileJson) {
  const { width, height, border } = fileJson;
  // convert mm to cm (/10), convert sqCM to sqM (/10000)
  return ((width / 10 + border) * (height / 10 + border)) / 10000;
}

function getUnderglassSurcharge(fileJson, basisPrice, surchargeUnderglass) {
  let surcharge = 0;
  if (fileJson.underglass) {
    surcharge =
      surchargeUnderglass.fix +
      (basisPrice * surchargeUnderglass.percentage) / 100;
  }
  return surcharge;
}

function getLaminateSurcharge(state, basisPrice, laminates) {
  const { laminate } = state;
  const selectedLaminate = laminates.filter((obj) => {
    return obj.key === laminate;
  })[0];
  return (
    selectedLaminate?.surcharge?.fix +
    (basisPrice * selectedLaminate?.surcharge?.percentage) / 100
  );
}

function getCuttingSurcharge(state, basisPrice, cuttings) {
  const { cutting } = state;
  const selectedCutting = cuttings.filter((obj) => {
    return obj.key === cutting;
  })[0];
  return (
    selectedCutting?.surcharge?.fix +
    (basisPrice * selectedCutting?.surcharge?.percentage) / 100
  );
}

const calculatePrice = (fileJson) => {
  const { editorType } = fileJson;
  let price = 0;
  let currentEditor = FILEEDITOR[editorType];
  if (currentEditor) {
    const editorBasePrice = currentEditor.basicAmount;
    let foilBasePrice = getFoilBasePrice(fileJson, currentEditor) * 1.45;
    const surfaceSize = getSurfaceSizeForPrice(fileJson);
    if (
      currentEditor.surfacePenaltyMinSize &&
      surfaceSize < currentEditor.surfacePenaltyMinSize
    ) {
      // (300 / 10 * 300 / 10 / 10000)
      price +=
        foilBasePrice *
        surfaceSize *
        (1 - (1 / currentEditor.surfacePenaltyMinSize) * surfaceSize);
    }

    foilBasePrice =
      foilBasePrice *
      (1 -
        (surfaceSize > 0.2
          ? surfaceSize > 2
            ? currentEditor.maxSurfaceDiscount || 0.25
            : surfaceSize / (2 / (currentEditor.maxSurfaceDiscount || 0.25))
          : 0));
    price += foilBasePrice * surfaceSize;

    const surcharges = currentEditor.sections.map((section) => {
      switch (section) {
        case "laminate":
          return getLaminateSurcharge(fileJson, price, currentEditor.laminates);
        case "cutting":
          return getCuttingSurcharge(fileJson, price, currentEditor.cuttings);
        case "underglass":
          return getUnderglassSurcharge(
            fileJson,
            price,
            currentEditor.surchargeUnderglass
          );
        default:
          return 0;
      }
    });

    price += surcharges.reduce((surcharge, sum) => surcharge + sum, 0);

    // Define constants for the multiplier function
    const maximum_penality = 1.4; // Starting multiplier for very small sizes
    const decrease_rate = 1.5; // Controls the rate of decrease
    const minumum_penality = 0.8; // Base multiplier for large sizes

    // Calculate the multiplier using the exponential function
    let multiplier =
      maximum_penality * Math.exp(-decrease_rate * surfaceSize) +
      minumum_penality;

    // Apply the multiplier to the base price
    price = editorBasePrice + price * multiplier;

    price += price * 0.1;

    const tax = 19 / 100;
    const discount = currentEditor.discount || 0;

    // // console.log("[MYF DEBUG] Before: ", surfaceSize, price);
    // if (surfaceSize < 0.05) {
    //   // console.log("[MYF DEBUG] 0.05");
    //   price = price * 1.95;
    // } else if (surfaceSize < 0.0575) {
    //   // console.log("[MYF DEBUG] 0.0575");
    //   price = price * 1.85;
    // } else if (surfaceSize < 0.065) {
    //   // console.log("[MYF DEBUG] 0.065");
    //   price = price * 1.75;
    // } else if (surfaceSize < 0.075) {
    //   // console.log("[MYF DEBUG] 0.075");
    //   price = price * 1.65;
    // } else if (surfaceSize < 0.085) {
    //   // console.log("[MYF DEBUG] 0.085");
    //   price = price * 1.45;
    // } else if (surfaceSize < 0.1) {
    //   // console.log("[MYF DEBUG] 0.1");
    //   price = price * 1.25;
    // } else if (surfaceSize < 0.2) {
    //   // console.log("[MYF DEBUG] 0.2");
    //   price = price * 1.15;
    // } else if (surfaceSize < 0.35) {
    //   // console.log("[MYF DEBUG] 0.35");
    //   price = price * 1.1;
    // } else {
    //   // console.log("[MYF DEBUG] default");
    //   price = price * 1.05;
    // }

    // console.log("[MYF DEBUG] after: ", surfaceSize, price);
    // TODO: add this back
    if (currentEditor.minPrice && currentEditor.minPrice > price) {
      price = currentEditor.minPrice;
    }

    if (currentEditor.roundPrice) {
      price = (Math.floor(price * (1 + tax)) + 0.99) / (1 + tax);
    }
    let priceObject = {
      price: price * (1 - discount / 100), // exclusice TAX / VAT
      priceBeforeDiscountAndTax: price, // price w/o discount and tax
      priceBeforeDiscount: price * (1 + tax), // price that should be shown including tax w/o discount
      priceInclVat: price * (1 - discount / 100) * (1 + tax), // incluseive TAX / VAT
      discountPercentage: discount, // discount in full percent
      discountAmount: price * (1 + tax) * (discount / 100), // discount amount
      vatPercentage: tax * 100, // 19% VAT in germany
      vatAmount: price * (1 - discount / 100) * tax, // 19% of price = VAT
    };

    let currentDiscount = {};
    if (currentEditor.discounts) {
      currentEditor.discounts.forEach((discount) => {
        if (discount.amount <= fileJson.amount) {
          currentDiscount = discount;
        }
      });
      // maxDiscount = selectedFileEditor.discounts[selectedFileEditor.discounts.length - 1];
    }

    priceObject.displayPrice =
      Math.round(
        priceObject.priceInclVat -
          (currentDiscount.percentage
            ? (priceObject.priceInclVat * currentDiscount.percentage) / 100
            : 0) +
          "e+2"
      ) + "e-2";
    priceObject.displayVat =
      Math.round(
        priceObject.vatAmount -
          (currentDiscount.percentage
            ? (priceObject.vatAmount * currentDiscount.percentage) / 100
            : 0) +
          "e+2"
      ) + "e-2";

    return priceObject;
    // return {displayPrice, finalPrice, Vat, priceBeforeDiscountAndTax}
  }
  return {
    displayPrice: 0,
    displayVat: 0,
    price: 0,
    priceBeforeDiscountAndTax: 0,
    priceBeforeDiscount: 0,
    priceInclVat: 0,
    discountPercentage: 0,
    discountAmount: 0,
    vatPercentage: 0,
    vatAmount: 0,
  };
};

function uniqueId(length) {
  let id;
  id = "";
  while (id.length < length) {
    id += Math.random().toString(36).substr(2);
  }
  return id.substr(0, length);
}

function generateSKU(slug) {
  return (
    (!slug || slug === "undefined" ? "designer" : slug) + "_" + uniqueId(25)
  );
}

function generateChecksum() {
  const checksum = sha1("hash me");
  return checksum;
}

function generateDescriptionText(fileJson, translations) {
  // 460mm x 443mm | Typ: Motiv als Digitaldruck auf Klebefolie | Schutzlaminat: Glänzend | Hinterglasverklebung: Nein | Form: Rechteckig | Rand: +0cm
  let description = "";
  const underglassVal = fileJson.underglass
    ? translations.yes
    : translations.no;
  let currentEditor = FILEEDITOR[fileJson.editorType];
  currentEditor.sections.forEach((section) => {
    switch (section) {
      case "size":
        description += `${translations.width}: ${Math.floor(
          fileJson.width
        )}mm (${(fileJson.width / 10).toFixed(1)}cm) x `;
        description += `${translations.height}: ${Math.floor(
          fileJson.height
        )}mm (${(fileJson.height / 10).toFixed(1)}cm) | `;
        break;
      case "sizeselect":
        description += `${translations.width}: ${Math.floor(
          fileJson.width
        )}mm (${(fileJson.width / 10).toFixed(1)}cm) x `;
        description += `${translations.height}: ${Math.floor(
          fileJson.height
        )}mm (${(fileJson.height / 10).toFixed(1)}cm) | `;
        break;
      case "laminate":
        description +=
          translations.laminate +
          ": " +
          translations[fileJson.laminate] +
          " | ";
        break;
      case "cutting":
        description +=
          translations.cutting + ": " + translations[fileJson.cutting] + " | ";
        break;
      case "vinyl":
        description +=
          translations.material +
          ": " +
          translations[COLORTYPES_NAMES[fileJson.materialSerie]] +
          " | ";
        break;
      case "color":
        const selectedColor = fileJson.color;
        description += `${translations.color}: ${selectedColor.title}(${selectedColor.farbcode}_${fileJson.materialSerie}) |`;
        break;
      case "underglass":
        if (fileJson.underglass) {
          description += translations.underglass + ": " + underglassVal + " | ";
        } // only show underglass message if selected since users are confused
        break;
      case "border":
        description += `${translations.border}: ${fileJson.border || "0"}cm | `;
        break;
      case "stripwidth":
        description += `${translations.stripwidth} | `;
        break;
      case "whiteprint_mode":
        description += `${
          translations["whiteprint_" + fileJson.whiteprint_mode]
        } | `;
        break;
      case "filecheck":
        // description += translations.filecheck + ': ' + fileJson.filecheck + ' | ';
        break;
      default:
        description += translations.missingSection + section + " | ";
        break;
    }
  });
  if (fileJson.skip_file_upload)
    description += translations.skip_file_upload + " | ";

  return description.slice(0, -3);
}
const generateJson = (fileJson, lang, translations, artwork, sku, cropping = {}) => {
  const calculatedPrice = calculatePrice(fileJson);
  let currentEditor = FILEEDITOR[fileJson.editorType];
  let currentType = FILEEDITORTYPENAMES[fileJson.editorType];
  const { sections } = currentEditor;

  const jsonObj = {
    width: fileJson.width,
    height: fileJson.height,
    amount: fileJson.amount || 1,
    origin: lang,
    productTitle: translations[currentType],
    description: generateDescriptionText(fileJson, translations),
    editorType: fileJson.editorType,
    preview: artwork?.data?.attributes?.preview ? (REACT_APP_COP_SERVER + artwork?.data?.attributes?.preview) : artwork?.preview, // or is fallback for old behaviour
    filename:
      artwork?.data?.attributes?.filename ||
      artwork?.imagename + "." + artwork?.filetype, // or is fallback for old behaviour
    sku: sku || generateSKU(),
    price: calculatedPrice["price"],
    priceBeforeDiscountAndTax: calculatedPrice["priceBeforeDiscountAndTax"],
    depth: generateChecksum(),
    vectorizeFile: fileJson.vectorizeFile,
  };

  if (sections.includes("border")) {
    jsonObj.border = fileJson.border;
  }
  if (sections.includes("underglass")) {
    jsonObj.underglass = fileJson.underglass || false;
  }
  if (sections.includes("vinyl")) {
    jsonObj.vinyl = fileJson.materialSerie;
  }
  if (sections.includes("cutting")) {
    jsonObj.cutting = fileJson.cutting;
  }
  if (sections.includes("color")) {
    jsonObj.color = fileJson.color?.farbcode;
  }
  if (sections.includes("laminate")) {
    jsonObj.laminate = fileJson.laminate;
  }
  if (sections.includes("filecheck")) {
    jsonObj.filecheck = fileJson.filecheck;
  }
  if (cropping?.crop) {
    const { croppedArea, croppedAreaPixels } = cropping.crop;
    jsonObj.fileCrop = {
      crop: {
        ...croppedArea,
        rotate: cropping.crop.rotation, //??
        // scaleX: 1, // from zoom??
        // scaleY: 1 // from zoom ??
      },
      image: {
        ...croppedAreaPixels,
        aspectRatio: jsonObj.fileRatio, // or croppedAreaPixels.width/ croppedAreaPixels.height ??
        // naturalHeight: 0, // ??
        // naturalWidth: 0, // ??
        // left: croppedAreaPixels.x, // ??
        // top: croppedArea.y, // ??
        // rotate: file.crop.rotation, // ??
        // scaleX: 1, // ??
        // scaleY: 1 // ??
      },
      rotation: cropping.crop.rotation,
      cropper: cropping.crop,

      // canvas: { // ??
      //   height: 0, // ??
      //   left: 0, // ??
      //   naturalHeight: 0, // ??
      //   naturalWidth: 0, // ??
      //   top: 0, // ??
      //   width: 0, // ??
      // }
    };
  }
  return jsonObj;
};

const formHelpers = {
  extractFormState: extractFormState,
  calculatePrice: calculatePrice,
  generateJson: generateJson,
  generateSKU: generateSKU,
};

export default formHelpers;
